import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useNotifications } from '@/contexts/NotificationsContext';
import { useLiveOverview } from '@/contexts/LiveOverviewContext';
import { useLiveEvents } from '@/hooks/useLiveEvents';
import {
  callUrl,
  notificationExternalUrl,
  notificationUrl,
  openTargetFromLocation,
  whatsappChatUrl,
} from '@/lib/notificationList';
import type { StaffNotification } from '@/services/notificationService';
import type { CallbackTicket } from '@/services/callbackTicketService';
import { ticketUrl } from '@/lib/callbackTicket';
import { NotificationPanel } from './NotificationPanel';
import { unreadLabel } from './notificationFormat';

/** A gentle single swing from the top, like a real bell — once per new urgent alert. */
const WIGGLE_KEYFRAMES: Keyframe[] = [
  { transform: 'rotate(0deg)' },
  { transform: 'rotate(-14deg)' },
  { transform: 'rotate(11deg)' },
  { transform: 'rotate(-7deg)' },
  { transform: 'rotate(3deg)' },
  { transform: 'rotate(0deg)' },
];

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function wiggle(element: Element | null) {
  if (!element || typeof (element as HTMLElement).animate !== 'function' || prefersReducedMotion()) return;
  (element as HTMLElement).animate(WIGGLE_KEYFRAMES, { duration: 700, easing: 'ease-in-out' });
}

/**
 * The bell in the app header: unread badge (brand accent while something
 * urgent is open), the „Jetzt live“ + „Benachrichtigungen“ popover, and the
 * „Ton aktivieren“ hint while the browser's autoplay lock swallowed a chime.
 *
 * Rendered inside the router (every page's Layout), so it is also where the
 * provider above the router learns which chat Verläufe has open and how to
 * navigate from a desktop-notification click.
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const location = useLocation();
  const notifications = useNotifications();
  const { overview, loading: overviewLoading } = useLiveOverview();
  const { status } = useLiveEvents();
  const [open, setOpen] = useState(false);
  const iconRef = useRef<SVGSVGElement>(null);
  const { noteChatOpened, registerNavigator, latestAlert, unreadCount, hasUrgentUnread, markRead, items } = notifications;

  const tickets = overview.callback_tickets;
  useEffect(() => {
    noteChatOpened(openTargetFromLocation(location.pathname, location.search, tickets));
  }, [location.pathname, location.search, tickets, noteChatOpened]);

  useEffect(() => registerNavigator((url) => navigate(url)), [navigate, registerNavigator]);

  // The bell re-mounts with every page (Layout); only an alert newer than
  // the one current at mount may swing it — not every navigation.
  const alertSeq = latestAlert?.seq ?? 0;
  const swungForRef = useRef(alertSeq);
  useEffect(() => {
    if (alertSeq === swungForRef.current) return;
    swungForRef.current = alertSeq;
    wiggle(iconRef.current);
  }, [alertSeq]);

  const go = useCallback(
    (url: string | null) => {
      setOpen(false);
      if (url) navigate(url);
    },
    [navigate],
  );

  const markUnreadWhere = useCallback(
    (match: (item: StaffNotification) => boolean) => {
      const ids = items.filter((item) => !item.read_at && match(item)).map((item) => item.id);
      if (ids.length > 0) void markRead(ids);
    },
    [items, markRead],
  );

  const openContact = (contactKey: string) => {
    markUnreadWhere((item) => item.contact_key === contactKey);
    go(whatsappChatUrl(contactKey));
  };

  const openCall = (callSid: string) => {
    markUnreadWhere((item) => item.call_sid === callSid);
    go(callUrl(callSid));
  };

  const openTicket = (ticket: CallbackTicket) => {
    markUnreadWhere((item) => item.call_sid === ticket.call_sid);
    go(ticketUrl(ticket));
  };

  const openNotification = (item: StaffNotification) => {
    if (!item.read_at) void markRead([item.id]);
    // A blocked number is a real link to Meta (see NotificationList): the
    // browser opens the tab itself, so nothing is done here beyond marking it
    // read — no navigation, and the panel stays for the way back.
    if (notificationExternalUrl(item)) return;
    go(notificationUrl(item, tickets));
  };

  const badge = unreadCount > 99 ? '99+' : String(unreadCount);
  const showSoundHint = notifications.soundBlocked && notifications.prefs.soundEnabled;

  return (
    <div className="flex items-center gap-1">
      {showSoundHint && (
        <button
          type="button"
          onClick={() => void notifications.enableSound()}
          aria-label="Ton aktivieren"
          className="inline-flex h-8 items-center gap-1.5 rounded-full border border-brand/30 bg-brand-soft px-2.5 text-xs font-medium text-brand transition-colors hover:bg-brand/[0.16] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
        >
          <VolumeX className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="hidden sm:inline">Ton aktivieren</span>
        </button>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative rounded-full text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
            aria-label={unreadCount > 0 ? `Benachrichtigungen – ${unreadLabel(unreadCount)}` : 'Benachrichtigungen'}
          >
            <Bell ref={iconRef} className="h-5 w-5 origin-top" aria-hidden="true" />
            {unreadCount > 0 && (
              <span
                aria-hidden="true"
                data-urgent={hasUrgentUnread || undefined}
                className={cn(
                  'absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full px-1 text-center text-[10px] font-bold leading-[18px] ring-2 ring-chrome tabular-nums',
                  hasUrgentUnread ? 'bg-brand text-brand-foreground' : 'bg-foreground/75 text-background',
                )}
              >
                {badge}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          sideOffset={8}
          collisionPadding={16}
          aria-label="Benachrichtigungen"
          className="w-[calc(100vw-2rem)] overflow-hidden rounded-2xl p-0 shadow-xl sm:w-[400px]"
        >
          <NotificationPanel
            notifications={notifications}
            overview={overview}
            overviewLoading={overviewLoading}
            status={status}
            onOpenContact={openContact}
            onOpenCall={openCall}
            onOpenTicket={openTicket}
            onOpenNotification={openNotification}
          />
        </PopoverContent>
      </Popover>
      {/* Mounted while empty: screen readers only announce changes inside an existing region. */}
      <span className="sr-only" role="status" aria-live="polite">
        {/* Keyed per alert: the same title growing again is announced again. */}
        {latestAlert && <span key={latestAlert.seq}>{`Neu: ${latestAlert.title}`}</span>}
      </span>
    </div>
  );
}

export default NotificationBell;
